import { useState, useEffect, useRef } from "react";
import "./AgentChat.css";
import "./LoadingDots.css";
import { useSelector } from "react-redux";
import { selectUser, selectMergeConnected } from "../../redux/features/appData";
import toast, { Toaster } from "react-hot-toast";
import useWebSocket from "react-use-websocket";
import { useParams } from "react-router-dom";
import {
  apiAISaveAgentResponse,
  apiGetConversationHistory,
  apiStartNewThread,
} from "../../services/AIService";
import { HumanMessage as HumanMessageComponent } from "./HumanMessage";
import { AIMessage } from "./AIMessage";
import { ToolMessage } from "./ToolMessage";
import { NewThreadMessage } from "./NewThreadMessage";
import {
  AgentResponseMessage,
  AgentToolCallMessage,
  AgentFinalResponseMessage,
  HumanMessage,
} from "./types";
import { MessageHistoryType } from "winslow-core";
import {
  addMessage,
  selectCurrentThread,
  selectEmployees,
  selectMessages,
  selectPersonas,
  selectPrompt,
  setEmployees,
  setMessages,
  setPersonas,
  setPrompt,
} from "../../redux/features/agent/agentSlice";
import { useDispatch } from "react-redux";
import { apiGetEmployees } from "../../services/EmployeeService";
import { apiGetPersonas } from "../../services/PersonasService";
import CirclePersonaEmployeeSelect from "../common/CirclePersonaEmployeeSelect";
import config from "environment";
import React from "react";

type AgentChatProps = {};

export const AgentChat = ({}: AgentChatProps) => {
  const storeUser = useSelector(selectUser);
  const dispatch = useDispatch();
  const messages = useSelector(selectMessages);
  const currentThread = useSelector(selectCurrentThread);
  const prompt = useSelector(selectPrompt);
  const employees = useSelector(selectEmployees);
  const personas = useSelector(selectPersonas);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [lastPrompt, setLastPrompt] = useState<string>("");

  const { collectionId } = useParams();

  const mergeConnected = useSelector(selectMergeConnected);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastAIMessageRef = useRef<HTMLDivElement>(null);
  const [lastMessageType, setLastMessageType] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const historyRef = useRef<HTMLDivElement>(null);

  const { sendJsonMessage, lastJsonMessage } =
    useWebSocket<AgentResponseMessage>(config.wsAgentUrl, {
      onOpen: () => console.log("[websocket] opened"),
      onClose: (code: any) => console.log("[websocket] closed: ", code),
      shouldReconnect: (closeEvent) => true,
      onError: (event: WebSocketEventMap["error"]) =>
        console.log("[websocket] error: ", event),
    });

  useEffect(() => {
    if (storeUser && collectionId) {
      const fetchData = async () => {
        try {
          // First fetch employees and personas in parallel
          if (storeUser.currentWorkspaceId) {
            const [employeesResult, personasResult] = await Promise.all([
              apiGetEmployees(storeUser.currentWorkspaceId),
              apiGetPersonas(storeUser.currentWorkspaceId),
            ]);

            if (employeesResult) {
              dispatch(setEmployees(employeesResult));
            }

            if (personasResult) {
              dispatch(setPersonas(personasResult));
            }
          }

          // Then fetch conversation history after employees and personas are loaded
          const conversationResult = await apiGetConversationHistory(
            storeUser.id,
            collectionId
          );
          if (conversationResult) {
            dispatch(setMessages(conversationResult));
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      };

      fetchData();
    }

    // Cleanup function to clear messages when component unmounts
    return () => {
      dispatch(setMessages([]));
    };
  }, [storeUser, collectionId, dispatch]);

  useEffect(() => {
    if (lastJsonMessage) {
      console.log("lastJsonMessage", lastJsonMessage);

      // Handle final response message
      if (lastJsonMessage.type === MessageHistoryType.AI_MESSAGE) {
        const aiMessage = lastJsonMessage as AgentFinalResponseMessage;
        dispatch(addMessage(aiMessage));
        setLastMessageType(MessageHistoryType.AI_MESSAGE);
        setIsLoading(false);
        apiAISaveAgentResponse({
          question: lastPrompt,
          response: aiMessage.finalResponse.answer,
          userId: storeUser!.id,
          originatorEmail: undefined,
          originatorName: undefined,
          workspaceId: storeUser!.currentWorkspaceId!,
          collectionId: collectionId!,
          linkRecordIds: aiMessage.finalResponse.sources,
          feedback: undefined,
          sources: aiMessage.finalResponse.sources,
        });
        return;
      }

      if (lastJsonMessage.type === MessageHistoryType.TOOL_CALL) {
        const toolMessage = lastJsonMessage as AgentToolCallMessage;
        dispatch(addMessage(toolMessage));
        setLastMessageType(MessageHistoryType.TOOL_CALL);
        return;
      }

      if (lastJsonMessage.type === MessageHistoryType.TOOL_RESULT) {
        return;
      }

      // Log unexpected message structure
      console.warn(
        "Received message with unexpected structure:",
        lastJsonMessage
      );
    }
  }, [lastJsonMessage]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Also scroll when loading state changes
  useEffect(() => {
    scrollToBottom();
  }, [isLoading]);

  // Function to scroll to the bottom of the chat
  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });

        // Focus on textarea after scrolling
        setTimeout(() => {
          textareaRef.current?.focus();
        }, 100);
      }
    }, 300);
  };

  useEffect(() => {
    if (currentThread) {
      console.log("currentThread", currentThread);
      // Scroll when thread changes
      scrollToBottom();
    }
  }, [currentThread]);

  const handleSendMessage = async () => {
    if (!prompt.trim() || !storeUser || !collectionId) {
      if (!storeUser) toast("No user available");
      if (!collectionId) toast("No collection selected");
      return;
    }

    setLastPrompt(prompt);

    // Create a human message to add to the UI
    const newHumanMessage: HumanMessage = {
      id: Date.now(),
      type: MessageHistoryType.USER_MESSAGE,
      sessionId: "",
      threadId: currentThread?.id || "",
      message: {
        id: ["langchain_core", "messages", "HumanMessage"],
        lc: 1,
        type: "constructor",
        kwargs: {
          content: prompt,
          additional_kwargs: {},
          response_metadata: {},
        },
      },
      finalResponse: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      messageThread: currentThread || undefined,
    };

    dispatch(addMessage(newHumanMessage));
    setLastMessageType(MessageHistoryType.USER_MESSAGE);

    dispatch(setPrompt(""));
    setIsLoading(true);

    sendJsonMessage({
      userId: storeUser.id,
      action: "ask",
      question: prompt,
      collectionId: collectionId,
      employeeId: currentThread?.employeeId,
      personaId: currentThread?.personaId,
      threadId: currentThread?.id,
    });
  };

  const handleOverviewClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleExpand = (message: AgentFinalResponseMessage) => {
    // Logic for expanding a message
    console.log("Message expanded:", message.id);
  };

  const handleStartNewThread = async (
    personaId: string | null,
    employeeId: string | null
  ) => {
    if (!storeUser || !collectionId) {
      if (!storeUser) toast("No user available");
      if (!collectionId) toast("No collection selected");
      return;
    }
    const newThreadMessage = await apiStartNewThread(
      storeUser.id,
      collectionId,
      personaId,
      employeeId
    );

    if (!newThreadMessage) {
      toast("Failed to start new thread");
      return;
    }

    dispatch(addMessage(newThreadMessage));
    //scroll to bottom
    scrollToBottom();
    setLastMessageType(MessageHistoryType.NEW_THREAD);
  };

  return (
    <div className="agent-chat">
      {/* <div className="agent-chat-header">
        
        <div className="agent-chat-header-actions">
          <button
            className="agent-chat-header-action"
            onClick={() => setShowOverview(!showOverview)}>
            Overview
          </button>
        </div>
      </div> */}

      <div className="agent-chat-content">
        <div className="agent-chat-messages">
          <div ref={historyRef} className="agent-chat-history-items">
            {messages.length === 0 && !isLoading ? (
              <div className="agent-chat-zero-state">
                <div className="agent-chat-zero-state-content">
                  <div className="agent-chat-zero-state-icon">
                    <img src="/icons/winslow-ai-logo.svg" alt="" />
                  </div>

                  <div className="agent-chat-zero-state-title">
                    No messages yet
                  </div>
                  <div className="agent-chat-zero-state-description">
                    Start a conversation by typing a message below.
                  </div>
                </div>
              </div>
            ) : (
              <>
                {messages.map((message, index) => {
                  // Skip rendering if this is the first message and it's a NEW_THREAD message
                  if (
                    index === 0 &&
                    message.type === MessageHistoryType.NEW_THREAD
                  ) {
                    return null;
                  }

                  return (
                    <React.Fragment key={message.id.toString()}>
                      {message.type === MessageHistoryType.USER_MESSAGE && (
                        <HumanMessageComponent
                          message={message}
                          handleStartNewThread={handleStartNewThread}
                        />
                      )}
                      {message.type === MessageHistoryType.AI_MESSAGE && (
                        <AIMessage message={message} onExpand={handleExpand} />
                      )}
                      {message.type === MessageHistoryType.TOOL_CALL && (
                        <ToolMessage
                          toolType={message.message.kwargs.tool_calls[0].name}
                        />
                      )}
                      {message.type === MessageHistoryType.NEW_THREAD && (
                        <NewThreadMessage message={message} />
                      )}
                    </React.Fragment>
                  );
                })}
                {isLoading && (
                  <div className="agent-chat-history-item">
                    <div className="loading-dots">
                      <div className="dot" />
                      <div className="dot" />
                      <div className="dot" />
                    </div>
                  </div>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="agent-chat-input">
          <div className="agent-chat-input-row">
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => dispatch(setPrompt(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder="Type your message..."
              disabled={isLoading}
              rows={1}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = "auto";
                const newHeight = Math.min(target.scrollHeight, 120);
                target.style.height = `${newHeight}px`;
              }}
            />
            <button
              onClick={handleSendMessage}
              disabled={isLoading || !prompt.trim()}>
              Send
            </button>
          </div>
          {config.showPersonas && (
            <div className="agent-chat-persona-selector">
              <CirclePersonaEmployeeSelect
                disabled={!mergeConnected}
                employees={employees}
                personas={personas}
                onChange={(value, isPersona) => {
                  if (value === "" || value === null) {
                    handleStartNewThread(null, null);
                    return;
                  }

                  if (isPersona) {
                    handleStartNewThread(value, null);
                  } else {
                    handleStartNewThread(null, value);
                  }
                }}
                selectedEmployee={currentThread?.employeeId}
                selectedPersona={currentThread?.personaId}
                includeNoFilter={true}
              />
            </div>
          )}
        </div>
      </div>

      <Toaster />
    </div>
  );
};
